import AppsContainer from '@nxsys/components/AppsContainer';
import { HMRCUpdateFilterHeader } from '../components/FilterHeader';
import AppCardTabList from '@nxsys/components/AppCardTabListContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { useCallback, useEffect, useMemo, useState } from 'react';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppTable from '@nxsys/components/AppTableContainer';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, AppState } from '@nxsys/store';
import { hmrcUpdatesActions } from '@nxsys/store/slices/hmrcUpdates/reducer/hmrcUpdatesSlice';
import { GetStudentLoanUpdatesParams } from '@nxsys/store/slices/hmrcUpdates/types';
import {
  getStudentLoanAppliedCancelledColumns,
  getStudentLoanPendingColumns,
} from './columns';
import { HMRCDataType } from '@nxsys/types/models/hmrcUpdates/p6p9';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import { OptionType } from '@nxsys/components/AppSelectSync';
import { ActionsHeader } from '../components/ActionsHeader';
import { useDebounce } from '@nxsys/hooks/useDebounce';
import { showToast } from '@nxsys/utils/toast';
import { StudentLoanRecordType } from '@nxsys/types/models/hmrcUpdates/studentLoan';
import AppConfirmationModalWithIcon from '@nxsys/components/AppConfirmationModalWithIcon';
import { ParagraphWithHighlight } from '@nxsys/components/AppConfirmationModal/index.styled';
import { APP_NAME_BUREAU } from '@nxsys/store/constants/keys';
import ModalSelectEmployee from '../components/ModalSelectEmployee';
import { resolveFilterCompanyIds } from '@nxsys/utils/companyScope';

type TabKey = 'pending' | 'appliedCancelled';

type StudentLoanFilterType = {
  companyRefs: OptionType | null;
  employeeRefs: OptionType | null;
  taxYear: string | null;
  issueDateFrom: string | null;
  issueDateTo: string | null;
  startDate: string | null;
  endDate: string | null;
  effectiveDateFrom: string | null;
  effectiveDateTo: string | null;
  NINumber: string | null;
};

export const StudentLoanUpdates = () => {
  const { messages, formatMessage } = useTypedIntl();
  const dispatch = useDispatch<AppDispatch>();
  const { isBureau, isBureauEmployer, user, isAgency } = useSelector(
    (state: AppState) => state.appStore,
  );

  const { studentLoan, loading } = useSelector(
    (state: AppState) => state.hmrcUpdatesStore,
  );

  const [activeTab, setActiveTab] = useState<TabKey>('pending');
  const [checkBoxHeader, setCheckBoxHeader] = useState(false);
  const [selectedRowIds, setSelectedRowIds] = useState<number[]>([]);
  const [searchValue, setSearchValue] = useState('');

  const [isOpenApplyModal, setIsOpenApplyModal] = useState(false);
  const [isOpenCancelModal, setIsOpenCancelModal] = useState(false);
  const [selectedRecord, setSelectedRecord] =
    useState<StudentLoanRecordType | null>(null);
  const [isOpenApplyAllModal, setIsOpenApplyAllModal] = useState(false);
  const [isOpenCancelAllModal, setIsOpenCancelAllModal] = useState(false);
  const [isOpenSelectEmployeeModal, setIsOpenSelectEmployeeModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<{ id: string; fullName?: string; forename?: string; surname?: string } | null>(null);

  const [filter, setFilter] = useState<StudentLoanFilterType>({
    companyRefs: null,
    employeeRefs: null,
    taxYear: null,
    issueDateFrom: null,
    issueDateTo: null,
    startDate: null,
    endDate: null,
    effectiveDateFrom: null,
    effectiveDateTo: null,
    NINumber: null,
  });

  const debouncedSearchTerm = useDebounce(searchValue, 500);

  const tabList: Array<{ key: TabKey; tab: string }> = [
    {
      tab: `${messages['hmrcUpdates.p6p9.pending']}`,
      key: 'pending',
    },
    {
      tab: `${messages['hmrcUpdates.p6p9.appliedCancelled']}`,
      key: 'appliedCancelled',
    },
  ];

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
      companyPaymentId: filter.companyRefs?.value || undefined,
      subcontractorId: filter.employeeRefs?.value || undefined,
      issueDateFrom: filter.issueDateFrom || undefined,
      issueDateTo: filter.issueDateTo || undefined,
      startDate: filter.startDate || undefined,
      endDate: filter.endDate || undefined,
      effectiveDateFrom: filter.effectiveDateFrom || undefined,
      effectiveDateTo: filter.effectiveDateTo || undefined,
      NINumber: filter.NINumber || undefined,
      search: debouncedSearchTerm,
    });
  }, [activeTab, filter, debouncedSearchTerm]);

  const handleFetchData = (params: GetStudentLoanUpdatesParams) => {
    const newParams = {
      ...params,
      companyPaymentId: params.companyPaymentId || undefined,
      subcontractorId: params.subcontractorId || undefined,
      issueDateFrom: params.issueDateFrom || undefined,
      issueDateTo: params.issueDateTo || undefined,
      startDate: params.startDate || undefined,
      endDate: params.endDate || undefined,
      effectiveDateFrom: params.effectiveDateFrom || undefined,
      effectiveDateTo: params.effectiveDateTo || undefined,
      search: params.search || undefined,
      appName: isBureau ? APP_NAME_BUREAU : undefined,
    };

    // The backend filters on companyRefs first (companyPaymentId is its deprecated
    // fallback), so the picked company has to travel as companyRefs to take effect
    newParams.companyRefs = resolveFilterCompanyIds(
      newParams.companyPaymentId ? [newParams.companyPaymentId] : undefined,
      isBureauEmployer,
      user?.companyUserIds,
    );

    if (activeTab === 'pending') {
      dispatch(hmrcUpdatesActions.getStudentLoanPending(newParams));
    } else {
      dispatch(hmrcUpdatesActions.getStudentLoanAppliedCancelled(newParams));
    }
  };

  const handleRefresh = () => {
    handleFetchData({
      page: 0,
      size: 20,
      companyPaymentId: filter.companyRefs?.value || undefined,
      subcontractorId: filter.employeeRefs?.value || undefined,
      issueDateFrom: filter.issueDateFrom || undefined,
      issueDateTo: filter.issueDateTo || undefined,
      startDate: filter.startDate || undefined,
      endDate: filter.endDate || undefined,
      effectiveDateFrom: filter.effectiveDateFrom || undefined,
      effectiveDateTo: filter.effectiveDateTo || undefined,
      NINumber: filter.NINumber || undefined,
      search: debouncedSearchTerm,
    });
  };

  const onTabChange = (key: TabKey) => {
    setActiveTab(key);
  };

  const renderData = useMemo(() => {
    return studentLoan[activeTab];
  }, [studentLoan, activeTab]);

  const onChangeTable = (sorter?: { field: string; order: 'desc' | 'asc' }) => {
    console.log(sorter);
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== renderData.pagination.pageSize;
    const shouldResetPage =
      isPageSizeChanged && renderData.pagination.current > 0;
    handleFetchData({
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
      companyPaymentId: filter.companyRefs?.value || undefined,
      subcontractorId: filter.employeeRefs?.value || undefined,
      issueDateFrom: filter.issueDateFrom || undefined,
      issueDateTo: filter.issueDateTo || undefined,
      startDate: filter.startDate || undefined,
      endDate: filter.endDate || undefined,
      effectiveDateFrom: filter.effectiveDateFrom || undefined,
      effectiveDateTo: filter.effectiveDateTo || undefined,
      NINumber: filter.NINumber || undefined,
      search: debouncedSearchTerm,
    });
  };

  const handleSelectAllRow = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setCheckBoxHeader(checked);
    if (checked) {
      setSelectedRowIds(
        (renderData.list as StudentLoanRecordType[]).map(
          (item: StudentLoanRecordType) => item.id,
        ),
      );
    } else {
      setSelectedRowIds([]);
    }
  };

  const handleSelectRow = (id: number, checked: boolean) => {
    if (checked) {
      setSelectedRowIds((prev) => [...prev, id]);
    } else {
      setSelectedRowIds((prev) => prev.filter((rowId) => rowId !== id));
      setCheckBoxHeader(false);
    }
  };

  const handleCompanyRefsChange = (value: OptionType | null) => {
    setFilter({ ...filter, companyRefs: value });
  };

  const handleEmployeeRefsChange = (value: OptionType | null) => {
    setFilter({ ...filter, employeeRefs: value });
  };

  const handleIssueDateFromChange = (value: string | null) => {
    setFilter({ ...filter, issueDateFrom: value, issueDateTo: value });
  };

  const handleIssueDateToChange = (value: string | null) => {
    setFilter({ ...filter, issueDateTo: value });
  };

  const handleStartDateChange = (value: string | null) => {
    setFilter({ ...filter, startDate: value });
  };

  const handleEndDateChange = (value: string | null) => {
    setFilter({ ...filter, endDate: value });
  };

  const handleEffectiveDateFromChange = (value: string | null) => {
    setFilter({ ...filter, effectiveDateFrom: value });
  };

  const handleEffectiveDateToChange = (value: string | null) => {
    setFilter({ ...filter, effectiveDateTo: value });
  };

  const handleNINumberChange = (value: string | null) => {
    setFilter({ ...filter, NINumber: value });
  };

  const handleApply = () => {
    if (selectedRowIds.length === 0) {
      showToast({ type: 'warning', message: messages['common.noSelected'] });
      return;
    }
    onApplyRecord(selectedRowIds);
  };

  const handleCancel = () => {
    if (selectedRowIds.length === 0) {
      showToast({ type: 'warning', message: messages['common.noSelected'] });
      return;
    }
    onCancelRecord(selectedRowIds);
  };

  const onApplyRecord = async (ids: number[]) => {
    setIsOpenApplyAllModal(false);
    await dispatch(hmrcUpdatesActions.applyStudentLoan({ ids }));
    setSelectedRowIds([]);
    setCheckBoxHeader(false);
    handleRefresh();
  };

  const onCancelRecord = async (ids: number[]) => {
    setIsOpenCancelAllModal(false);
    await dispatch(hmrcUpdatesActions.cancelStudentLoan({ ids }));
    setSelectedRowIds([]);
    setCheckBoxHeader(false);
    handleRefresh();
  };

  const handleApplyItem = (record: StudentLoanRecordType) => {
    setSelectedRecord(record);
    // Skip employee selection modal if record already has a linked employee
    if (record.subcontractorRef?.id) {
      setSelectedEmployee(record.subcontractorRef);
      setIsOpenApplyModal(true);
    } else {
      setIsOpenSelectEmployeeModal(true);
    }
  };

  const handleEmployeeSelected = (employee: { id: string; fullName?: string; forename?: string; surname?: string }) => {
    setSelectedEmployee(employee);
    setIsOpenSelectEmployeeModal(false);
    setIsOpenApplyModal(true);
  };

  const handleCancelItem = async (record: StudentLoanRecordType) => {
    setSelectedRecord(record);
    setIsOpenCancelModal(true);
  };

  const handleSync = async () => {
    await dispatch(
      hmrcUpdatesActions.hmrcSync({ hmrcDataType: HMRCDataType.STUDENT_LOAN }),
    );
    handleRefresh();
  };

  const toggleApplyAllModal = () => {
    if (selectedRowIds.length === 0) {
      showToast({
        type: 'warning',
        message: messages['common.noSelected'],
      });
      return;
    }
    setIsOpenApplyAllModal(!isOpenApplyAllModal);
  };

  const toggleCancelAllModal = () => {
    if (selectedRowIds.length === 0) {
      showToast({
        type: 'warning',
        message: messages['common.noSelected'],
      });
      return;
    }
    setIsOpenCancelAllModal(!isOpenCancelAllModal);
  };

  const renderColumns = useMemo(() => {
    if (activeTab === 'pending') {
      return getStudentLoanPendingColumns({
        handleApply: handleApplyItem,
        handleCancel: handleCancelItem,
        messages,
        checkBoxHeader,
        handleSelectAllRow,
        handleSelectRow,
        selectedRowIds,
        isBureau,
      });
    }
    return getStudentLoanAppliedCancelledColumns({
      messages,
      isBureau,
      isAgency,
      handleApply: handleApplyItem,
      handleCancel: handleCancelItem,
    });
  }, [
    activeTab,
    checkBoxHeader,
    handleSelectAllRow,
    handleSelectRow,
    selectedRowIds,
    onApplyRecord,
    onCancelRecord,
    isBureau,
  ]);

  const renderTitle = useCallback(() => {
    return (
      <AppPageTitle
        prefix={messages['hmrc.title']}
        separator=" - "
        title={messages['sidebar.app.hmrcUpdate.studentLoan']}
      />
    );
  }, [messages]);

  const setRowClassName = useCallback((record: StudentLoanRecordType) => {
    if (record?.isWarning) {
      return 'table-row__warning';
    }
    // if (record?.isError) {
    //   return 'table-row__error';
    // }
    return '';
  }, []);

  const isFiltering =
    !!renderData.queryParams?.search ||
    !!renderData.queryParams?.companyPaymentId ||
    !!renderData.queryParams?.subcontractorId ||
    !!renderData.queryParams?.issueDateFrom ||
    !!renderData.queryParams?.issueDateTo ||
    !!renderData.queryParams?.startDate ||
    !!renderData.queryParams?.endDate ||
    !!renderData.queryParams?.effectiveDateFrom ||
    !!renderData.queryParams?.effectiveDateTo ||
    !!renderData.queryParams?.NINumber;

  return (
    <AppsContainer fullView type="bottom" title={renderTitle()}>
      <HMRCUpdateFilterHeader
        companyRefs={filter.companyRefs}
        onCompanyRefsChange={handleCompanyRefsChange}
        employeeRefs={filter.employeeRefs}
        onEmployeeRefsChange={handleEmployeeRefsChange}
        issueDateFrom={filter.issueDateFrom}
        onIssueDateFromChange={handleIssueDateFromChange}
        issueDateTo={filter.issueDateTo}
        onIssueDateToChange={handleIssueDateToChange}
        startDate={filter.startDate}
        onStartDateChange={handleStartDateChange}
        endDate={filter.endDate}
        onEndDateChange={handleEndDateChange}
        effectiveDateFrom={filter.effectiveDateFrom}
        onEffectiveDateFromChange={handleEffectiveDateFromChange}
        effectiveDateTo={filter.effectiveDateTo}
        onEffectiveDateToChange={handleEffectiveDateToChange}
        NINumber={filter.NINumber}
        onNINumberChange={handleNINumberChange}
      />
      <AppCardTabList
        tabList={tabList}
        activeTab={activeTab}
        handleTabChange={onTabChange}
      >
        <AppsHeader>
          <ActionsHeader
            onApply={toggleApplyAllModal}
            onCancel={toggleCancelAllModal}
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            isShowActions={activeTab === 'pending'}
            onSync={handleSync}
          />
        </AppsHeader>
        <AppsContent>
          <AppTable
            data={renderData.list || []}
            loading={loading}
            page={renderData.pagination.current}
            total={renderData.pagination.totalDocs}
            pageSize={renderData.pagination.pageSize}
            scroll={{ x: 2500, y: 700 }}
            titleEmpty={messages[`hmrcUpdates.studentLoan.empty.${activeTab}`]}
            columns={renderColumns}
            onChangeTable={onChangeTable}
            onChangePagination={onChangePagination}
            rowClassName={setRowClassName}
            noDataType={isFiltering ? 'noDataFound' : 'noDataToShow'}
          />
        </AppsContent>
      </AppCardTabList>
      <AppConfirmationModalWithIcon
        title={messages['hmrc.studentLoan.apply.title']}
        paragraph={formatMessage(
          { id: 'hmrc.studentLoan.apply.confirm' },
          {
            employeeName: (
              <ParagraphWithHighlight>
                {selectedEmployee?.fullName || `${selectedEmployee?.forename || ''} ${selectedEmployee?.surname || ''}`.trim()}
              </ParagraphWithHighlight>
            ),
          },
        )}
        open={isOpenApplyModal}
        type="success"
        icon="IconCheck"
        onConfirm={async () => {
          if (selectedRecord?.id && selectedEmployee?.id) {
            await dispatch(
              hmrcUpdatesActions.applySingleStudentLoan({
                id: selectedRecord.id,
                employeeId: selectedEmployee.id,
              }),
            );
            handleRefresh();
          }
          setIsOpenApplyModal(false);
          setSelectedRecord(null);
          setSelectedEmployee(null);
        }}
        onDeny={() => {
          setIsOpenApplyModal(false);
          setSelectedRecord(null);
          setSelectedEmployee(null);
        }}
      />
      <AppConfirmationModalWithIcon
        title={messages['hmrc.studentLoan.cancel.title']}
        paragraph={formatMessage(
          { id: 'hmrc.studentLoan.cancel.confirm' },
          {
            employeeName: (
              <ParagraphWithHighlight>
                {`${selectedRecord?.subcontractorRef?.forename} ${selectedRecord?.subcontractorRef?.surname}`}
              </ParagraphWithHighlight>
            ),
          },
        )}
        open={isOpenCancelModal}
        type="error"
        icon="IconClose"
        onConfirm={() => {
          if (selectedRecord?.id) {
            onCancelRecord([selectedRecord?.id]);
          }
          setIsOpenCancelModal(false);
          setSelectedRecord(null);
        }}
        onDeny={() => {
          setIsOpenCancelModal(false);
          setSelectedRecord(null);
        }}
      />
      <AppConfirmationModalWithIcon
        title={messages['hmrc.studentLoan.apply.title']}
        paragraph={messages['hmrc.studentLoan.apply.selected.confirm']}
        open={isOpenApplyAllModal}
        type="success"
        icon="IconCheck"
        onConfirm={handleApply}
        onDeny={() => {
          setIsOpenApplyAllModal(false);
        }}
      />
      <AppConfirmationModalWithIcon
        title={messages['hmrc.studentLoan.cancel.title']}
        paragraph={messages['hmrc.studentLoan.cancel.selected.confirm']}
        open={isOpenCancelAllModal}
        type="error"
        icon="IconClose"
        onConfirm={handleCancel}
        onDeny={() => {
          setIsOpenCancelAllModal(false);
        }}
      />
      <ModalSelectEmployee
        isOpen={isOpenSelectEmployeeModal}
        toggle={() => {
          setIsOpenSelectEmployeeModal(false);
          setSelectedRecord(null);
        }}
        type="STUDENT_LOAN"
        onConfirm={handleEmployeeSelected}
      />
    </AppsContainer>
  );
};
